import { ReportingBarTableCell } from "@/pages/reporting/components/ReportingBarTableCell.tsx";
import { useRecruitingReports } from "@/pages/reporting/recruiting/recruiting-reports.hook.ts";
import {
  ApiAnalyticsRecruitingSourceEfficiencyReport,
  ApiAnalyticsRecruitingSourceEfficiencyReportRowItem,
} from "@/types/analytics/analytics.recruiting.types.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiPipelineStage } from "@/types/recruit/stages.types.ts";
import { Table, Text } from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface RecruitingSourceEfficiencyReportTableProps {
  report: ApiAnalyticsRecruitingSourceEfficiencyReport | undefined;
  isLoading: boolean;
}

export interface VMRecruitingSourceEfficiencyReportRowItem {
  item: ApiAnalyticsRecruitingSourceEfficiencyReportRowItem;
  width: number;
}

export interface VMRecruitingSourceEfficiencyReportRow {
  sources: VMRecruitingSourceEfficiencyReportRowItem[];
  pipeline_stage: ApiPipelineStage;
  total: number;
  width: number;
  applicants_ids: number[];
}

export const RecruitingSourceEfficiencyReportTable = ({
  report,
  isLoading,
}: RecruitingSourceEfficiencyReportTableProps) => {
  const { handlePreviewApplicants } = useRecruitingReports();
  const { t } = useTranslation();

  const calculatedReportRows = useMemo(() => {
    if (!report) return [];

    const rows: VMRecruitingSourceEfficiencyReportRow[] = [];
    const dayNumbers = report.report.rows.flatMap((e) => e.sources).map((item) => item.value);

    const maxPerSource = Math.max(...dayNumbers, 1);

    if (dayNumbers.length == 0) return [];
    const widthPerDay = Math.round(ColumnSizes.narrow.size / maxPerSource);

    for (let i = 0; i < report.report.rows.length; i++) {
      const row = report.report.rows[i];

      const rowItems: VMRecruitingSourceEfficiencyReportRowItem[] = [];

      for (let j = 0; j < report.config.sources.length; j++) {
        const sourceConfig = report.config.sources[j];
        const sourceItem = row.sources.find((e) => e.source.id == sourceConfig.id);
        if (sourceItem) {
          rowItems.push({
            item: sourceItem,
            width: sourceItem.value > 0 ? Math.round(sourceItem.value * widthPerDay) + 10 : 2,
          });
        }
      }

      const total = row.sources.reduce((acc, item) => acc + item.value, 0);
      const applicantIds = row.sources.flatMap((e) => e.applicants_ids);
      rows.push({
        sources: rowItems,
        pipeline_stage: row.pipeline_stage,
        total: total,
        width: total > 0 ? Math.round(total * widthPerDay) + 10 : 2,
        applicants_ids: applicantIds,
      });
    }

    return rows;
  }, [report]);

  const columnHelper = createColumnHelper<VMRecruitingSourceEfficiencyReportRow>();
  const columns = useMemo<ColumnDef<VMRecruitingSourceEfficiencyReportRow>[]>(() => {
    const inner: DisplayColumnDef<VMRecruitingSourceEfficiencyReportRow, unknown>[] = [];
    inner.push(
      columnHelper.display({
        id: "pipeline_stage",
        header: t(""),
        cell: (cell) => (
          <Text className={"text-default"}>{cell.row.original.pipeline_stage.title}</Text>
        ),
        ...ColumnSizes.employee,
      })
    );

    inner.push(
      columnHelper.display({
        id: `stage-total`,
        header: () => <Text>{t("common:total")}</Text>,
        cell: (cell) => (
          <>
            {cell.row.original.total > 0 && (
              <ReportingBarTableCell
                title={cell.row.original.total.toString()}
                width={cell.row.original.width}
                variant={"primary"}
                onClick={() => {
                  handlePreviewApplicants(
                    cell.row.original.applicants_ids,
                    AnalyticReportTypes.recruitingSourceEfficiency
                  );
                }}
              />
            )}
          </>
        ),
        enableResizing: false,
        ...ColumnSizes.narrow,
      })
    );

    if (report?.config && report.config.sources.length > 0) {
      for (let i = 0; i < report.config.sources.length; i++) {
        const source = report.config.sources[i];
        inner.push(
          columnHelper.display({
            id: `stage-${i}`,
            header: () => <Text className={"capitalize"}>{source.title}</Text>,
            cell: (cell) => (
              <>
                {cell.row.original.sources[i].item ? (
                  <ReportingBarTableCell
                    title={cell.row.original.sources[i].item.value.toString()}
                    width={cell.row.original.sources[i].width}
                    variant={"secondary"}
                    onClick={() => {
                      handlePreviewApplicants(
                        cell.row.original.sources[i].item?.applicants_ids || [],
                        AnalyticReportTypes.recruitingSourceEfficiency
                      );
                    }}
                  />
                ) : (
                  <ReportingBarTableCell title={"-"} variant={"secondary"} width={2} />
                )}
              </>
            ),
            ...ColumnSizes.status,
          })
        );
      }
    }
    return inner;
  }, [report, calculatedReportRows]);

  return (
    <Table
      data={calculatedReportRows || []}
      columns={columns}
      showSkeleton={isLoading}
      stickyFirstColumn={true}
    />
  );
};
